import { logger } from '@/utils/logger';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import React, { useRef, Suspense, lazy } from "react";
import { reloadForStaleChunk } from "@/utils/staleChunkRecovery";


type AnyProps = Record<string, unknown>;

function lazyWithRetry(
  importFn: () => Promise<{ default: React.ComponentType<AnyProps> }>,
  retries = 2,
  interval = 800,
): React.LazyExoticComponent<React.ComponentType<AnyProps>> {
  return lazy(() =>
    new Promise<{ default: React.ComponentType<AnyProps> }>((resolve, reject) => {
      const attempt = (remaining: number) => {
        importFn()
          .then(resolve)
          .catch((err: unknown) => {
            if (remaining <= 0) {
              if (!reloadForStaleChunk()) reject(err);
              return;
            }
            setTimeout(() => attempt(remaining - 1), interval);
          });
      };
      attempt(retries);
    }),
  );
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
          <h2 className="text-2xl font-bold">Something went wrong</h2>
          <p className="text-muted-foreground max-w-md">{this.state.error?.message || 'An unexpected error occurred.'}</p>
          <button
            className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import AuthGuard from "./components/AuthGuard";
import { AppProvider } from "./contexts/AppContext";
import { useAuthGuard } from "./hooks/useAuthGuard";
import { authApi } from "@/api/auth/auth";
import Loading from "./components/ui/loading";
import GrafanaSignedOut from "./pages/monitoring/GrafanaSignedOut";
import 'maplibre-gl/dist/maplibre-gl.css';
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const VideoUpload = lazyWithRetry(() => import("./pages/upload/Upload"));
const Analysis = lazyWithRetry(() => import("./pages/analysis/Analysis"));
const Simulation = lazyWithRetry(() => import("./pages/simulation/Simulation"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Reports = lazyWithRetry(() => import("./pages/Reports"));
const Login = lazyWithRetry(() => import("./pages/auth/Login"));
const Signup = lazyWithRetry(() => import("./pages/auth/Signup"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const EmailVerification = lazyWithRetry(() => import("./pages/EmailVerification"));
const AccountVerified = lazyWithRetry(() => import("./pages/AccountVerified"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const SystemDashboardPage = lazyWithRetry(() => import("./pages/admin/SystemDashboard").then(module => ({ default: module.SystemDashboardPage })));
const UserManagementPage = lazyWithRetry(() => import("./pages/admin/UserManagement").then(module => ({ default: module.UserManagementPage })));
const VideoManagementPage = lazyWithRetry(() => import("./pages/admin/VideoManagement").then(module => ({ default: module.VideoManagementPage })));
const S3AdminPage = lazyWithRetry(() => import("./pages/admin/S3AdminPage").then(module => ({ default: module.S3AdminPage })));
const Monitoring = lazyWithRetry(() => import("./pages/Monitoring"));
const FleetManagement = lazyWithRetry(() => import("./pages/FleetManagement"));
const UserVideosPage = lazyWithRetry(() => import("./pages/admin/UserVideos"));
const VideoDetailsPage = lazyWithRetry(() => import("./pages/admin/VideoDetails"));
const UserVideoDetailsPage = lazyWithRetry(() => import("./pages/user/VideoDetails"));
const MyVideosPage = lazyWithRetry(() => import("./pages/user/MyVideos"));
const SiteSurvey = lazyWithRetry(() => import("./pages/site/Survey"));
const HydraulicAnalysis = lazyWithRetry(() => import("./pages/hydraulic/MultiPlacementAnalysis"));
const AuditLogs = lazyWithRetry(() => import("./pages/admin/AuditLogs"));
const HydroPowerPlants = lazyWithRetry(() => import("./pages/hydropower/HydroPowerPlants"));
const SiteRanking = lazyWithRetry(() => import("./pages/hydropower/SiteRanking"));
const SiteAnalysis = lazyWithRetry(() => import("./pages/hydropower/SiteAnalysis"));
const HktCalculator = lazyWithRetry(() => import("./pages/admin/HktCalculator"));
const LiveView = lazyWithRetry(() => import("./pages/LiveView"));
const InfluxDBExplorerPage = lazyWithRetry(() => import("./pages/admin/InfluxDBExplorer").then(module => ({ default: module.InfluxDBExplorerPage })));
import { DataAccessGuard } from '@/components/admin/DataAccessGuard';
import { NonEngineerGuard } from '@/components/NonEngineerGuard';
import { PermissionGuard } from '@/components/guards/PermissionGuard';
import { Permission } from '@/lib/rbac';
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary';

const AuthenticatedRedirect = () => {
  const token = localStorage.getItem('auth_token');
  const isAuthenticated = token && authApi.isAuthenticated();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Parse role from JWT token to determine redirect destination
  try {
    const base64Payload = token!.split('.')[1];
    const padded = base64Payload + '='.repeat((4 - base64Payload.length % 4) % 4);
    const payload = JSON.parse(atob(padded));
    const userRole = payload.user?.role || payload.role;

    // Redirect engineers to site registration
    if (userRole === 'engineer') {
      return <Navigate to="/upload" replace />;
    }
  } catch (e) {
    // If token parsing fails, fall back to dashboard
    logger.warn('Failed to parse token for redirect:', e);
  }

  return <Navigate to="/dashboard" replace />;
};



const queryClient = new QueryClient();

const App = () => {
  const imageRef = useRef<HTMLImageElement>(null);
  useAuthGuard();

  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppProvider imageRef={imageRef}>
            <Suspense fallback={<Loading />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<AuthenticatedRedirect />} />
                <Route path="/login" element={<Login />} />
                {/*
                
                */}
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/api/v1/auth/verify/:token" element={<EmailVerification />} />
                <Route path="/account-verified/:token" element={<AccountVerified />} />
                <Route path="/monitoring/signed-out" element={<GrafanaSignedOut />} />

                {/* Protected routes with AuthGuard protection */}
                <Route path="/dashboard" element={<AuthGuard><NonEngineerGuard><Dashboard /></NonEngineerGuard></AuthGuard>} />
                {/* Allow engineers to access these core tools */}
                <Route path="/upload" element={<AuthGuard><VideoUpload /></AuthGuard>} />
                <Route path="/analysis" element={<AuthGuard><Analysis /></AuthGuard>} />
                <Route path="/simulation" element={<AuthGuard><RouteErrorBoundary routeName="simulation"><Simulation /></RouteErrorBoundary></AuthGuard>} />
                <Route path="/hydraulic" element={<AuthGuard><HydraulicAnalysis /></AuthGuard>} />
                <Route path="/reports" element={<AuthGuard><NonEngineerGuard><Reports /></NonEngineerGuard></AuthGuard>} />
                <Route path="/monitoring" element={<AuthGuard><PermissionGuard permission={Permission.MONITORING_READ}><Monitoring /></PermissionGuard></AuthGuard>} />
                <Route path="/fleet" element={<AuthGuard><NonEngineerGuard><FleetManagement /></NonEngineerGuard></AuthGuard>} />
                <Route path="/profile" element={<AuthGuard><NonEngineerGuard><Profile /></NonEngineerGuard></AuthGuard>} />
                <Route path="/site-data" element={<AuthGuard><NonEngineerGuard><MyVideosPage /></NonEngineerGuard></AuthGuard>} />


                {/* Admin routes */}
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/dashboard" element={<AuthGuard><SystemDashboardPage /></AuthGuard>} />
                <Route path="/admin/users" element={<AuthGuard><UserManagementPage /></AuthGuard>} />
                <Route path="/admin/videos" element={<AuthGuard><VideoManagementPage /></AuthGuard>} />
                <Route path="/admin/s3" element={<AuthGuard><S3AdminPage /></AuthGuard>} />
                <Route path="/admin/users-videos" element={<AuthGuard><UserVideosPage /></AuthGuard>} />
                <Route path="/admin/videos/:videoUid" element={<AuthGuard><DataAccessGuard><VideoDetailsPage /></DataAccessGuard></AuthGuard>} />
                <Route path="/admin/site-survey" element={<AuthGuard><DataAccessGuard><SiteSurvey /></DataAccessGuard></AuthGuard>} />
                <Route path="/admin/audit" element={<AuthGuard><DataAccessGuard><AuditLogs /></DataAccessGuard></AuthGuard>} />
                <Route path="/admin/hydropower" element={<AuthGuard><PermissionGuard permission={Permission.HYDROPOWER_READ}><HydroPowerPlants /></PermissionGuard></AuthGuard>} />
                <Route path="/admin/site-ranking" element={<AuthGuard><PermissionGuard permission={Permission.HYDROPOWER_READ}><SiteRanking /></PermissionGuard></AuthGuard>} />
                <Route path="/admin/site-analysis" element={<AuthGuard><PermissionGuard permission={Permission.HYDROPOWER_READ}><SiteAnalysis /></PermissionGuard></AuthGuard>} />
                <Route path="/admin/hkt-calculator" element={<AuthGuard><PermissionGuard permission={Permission.HYDROPOWER_READ} roles={['admin', 'engineer']}><HktCalculator /></PermissionGuard></AuthGuard>} />
                <Route path="/admin/live-view" element={<AuthGuard><PermissionGuard permission={Permission.MONITORING_READ} roles={['admin', 'engineer']}><LiveView /></PermissionGuard></AuthGuard>} />
                <Route path="/admin/influxdb" element={<AuthGuard><PermissionGuard permission={Permission.MONITORING_READ} roles={['admin', 'engineer']}><InfluxDBExplorerPage /></PermissionGuard></AuthGuard>} />

                {/* User-scoped details route */}
                <Route path="/videos/:videoUid" element={<AuthGuard><NonEngineerGuard><UserVideoDetailsPage /></NonEngineerGuard></AuthGuard>} />

                {/* Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AppProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;

